// CourseFullViewer.jsx - Design compact avec sommaire sticky
import React, { useEffect, useState, useRef } from "react";
import courseService from "../../services/course.service";
import {
  Typography,
  Button,
  Collapse,
  Tabs,
  Input,
  Avatar,
  Alert,
  Spin,
  Modal,
} from "antd";
import {
  PaperClipOutlined,
  ReadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  BookOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  MessageOutlined,
  SendOutlined,
  GiftOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  LinkOutlined,
} from "@ant-design/icons";

import QuizzesSubject from "./QuizzesSubject";
import TestDeConnaissance from "./TestDeConnaissance";
import TrainingViewer from "./TrainingViewer";
import EvaluationPreviewComponent from "./EvaluationPreviewComponent";
import GeneratedCertificate from "./GeneratedCertificate";
import { useSelector } from "react-redux";
import BadgeEligibility from "./BadgeEligibility";
import SecureContent from "../../common/SecureContent";

const { Text } = Typography;
const { Panel } = Collapse;

const footerElements = [
  { key: "eval", label: "Évaluation", icon: <BarChartOutlined />, color: "#52c41a", bgColor: "#f6ffed", borderColor: "#b7eb8f" },
  { key: "badge", label: "Badge", icon: <CrownOutlined />, color: "#faad14", bgColor: "#fffbe6", borderColor: "#ffe58f" },
  { key: "certif", label: "Certificat", icon: <GiftOutlined />, color: "#1890ff", bgColor: "#f0f5ff", borderColor: "#adc6ff" },
];

const flattenSubjects = (structure) => {
  const seen = new Set();
  const flat = [];
  structure.forEach((chapter) => {
    chapter.subjects.forEach((subject) => {
      const key = `${subject.id}-${subject.title}`;
      if (!seen.has(key)) { flat.push(subject); seen.add(key); }
      (subject.children || []).forEach((child) => {
        const childKey = `${child.id}-${child.title}`;
        if (!seen.has(childKey)) { flat.push(child); seen.add(childKey); }
      });
    });
  });
  return flat;
};

const extractLinkFromContent = (content) => {
  if (!content) return { link: "", cleanContent: "" };

  const linkRegex = /<p>\s*<a\s+href="([^"]+)"[^>]*>[^<]*<\/a>\s*<\/p>/i;
  const match = content.match(linkRegex);
  if (match) {
    return { link: match[1], cleanContent: content.replace(match[0], "").trim() };
  }

  const urlRegex = /<p>\s*(https?:\/\/[^\s<]+)\s*<\/p>/i;
  const urlMatch = content.match(urlRegex);
  if (urlMatch) {
    return { link: urlMatch[1], cleanContent: content.replace(urlMatch[0], "").trim() };
  }

  const boldUrlRegex = /<p>(?:<strong>)?[^<]*?:\s*(?:<\/strong>)?\s*<\/p>\s*<p>\s*(https?:\/\/[^\s<]+)\s*<\/p>/i;
  const boldMatch = content.match(boldUrlRegex);
  if (boldMatch) {
    return { link: boldMatch[1], cleanContent: content.replace(boldMatch[0], "").trim() };
  }

  const anyLinkRegex = /href="(https?:\/\/[^"]+)"/i;
  const anyMatch = content.match(anyLinkRegex);
  if (anyMatch) {
    const blockRegex = /<p>[^<]*<a\s+href="[^"]*"[^>]*>[^<]*<\/a>[^<]*<\/p>/gi;
    const cleanContent = content.replace(blockRegex, "").trim();
    return { link: anyMatch[1], cleanContent };
  }

  const rawUrlRegex = /(https?:\/\/[^\s<"']+)/i;
  const rawMatch = content.match(rawUrlRegex);
  if (rawMatch) {
    const pWithUrl = new RegExp(`<p>[^<]*${rawMatch[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^<]*<\/p>`, 'i');
    const cleanContent = content.replace(pWithUrl, "").trim();
    return { link: rawMatch[1], cleanContent };
  }

  return { link: "", cleanContent: content };
};

const CourseFullViewer = ({ courseId, courseTitle }) => {
  const [activeTab, setActiveTab] = useState("content");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [readItems, setReadItems] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [viewMode, setViewMode] = useState("content");
  const [footerView, setFooterView] = useState(null);
  const [commentInput, setCommentInput] = useState("");
  const [courseStructure, setCourseStructure] = useState([]);
  const [flatList, setFlatList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizResults, setQuizResults] = useState({});
  const [courseQuizzes, setCourseQuizzes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [openedCollapseKey, setOpenedCollapseKey] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const noteData = useSelector((state) => state.note.noteData);
  const contentRef = useRef(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [myNote, setMyNote] = useState(noteData?.noteFinale ?? 0);
  const [threshold, setThreshold] = useState(null);
  const [loadedResources, setLoadedResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [loadedAttachments, setLoadedAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  // ✅ FIX : userId déclaré ICI (avant tous les useEffect qui l'utilisent)
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  // ✅ FIX : Synchroniser myNote avec Redux quand noteData change
  useEffect(() => {
    if (noteData?.noteFinale !== undefined) {
      setMyNote(noteData.noteFinale);
    }
  }, [noteData]);

  // ✅ FIX : Charger la note réelle depuis le backend au montage
  useEffect(() => {
    const fetchMyNote = async () => {
      if (!courseId || !userId) return;
      try {
        const result = await courseService.CalculeNoteFinale(courseId, userId);
        console.log("📊 Résultat note finale brut:", result);
        // CalculeNoteFinale retourne un tableau de NoteFinaleDTO
        if (Array.isArray(result) && result.length > 0) {
          // Chercher la ligne "Total" ou prendre noteFinale du premier élément
          const totalRow = result.find(r => r.type?.toLowerCase() === 'total');
          const note = totalRow?.noteFinale ?? result[0]?.noteFinale ?? 0;
          setMyNote(note);
          console.log("✅ Note finale chargée:", note);
        } else if (result?.noteFinale !== undefined) {
          setMyNote(result.noteFinale);
          console.log("✅ Note finale chargée:", result.noteFinale);
        }
      } catch (err) {
        console.error("Erreur récupération note finale:", err);
      }
    };
    fetchMyNote();
  }, [courseId, userId]);

  useEffect(() => {
    if (footerView === "badge" && courseId) {
      const fetchThreshold = async () => {
        try {
          const result = await courseService.getLastThresholdCertificat(courseId);
          if (result?.threshold !== undefined) setThreshold(result.threshold);
        } catch (err) { console.error("Erreur threshold:", err); }
      };
      fetchThreshold();
    }
  }, [footerView, courseId]);

  const handleViewAttachments = async (subjectItem, parentSubject) => {
    setSelectedSubject(subjectItem);
    setViewMode("attachments");
    setActiveTab("content");
    setAttachmentsLoading(true);
    try {
      let data = [];
      if (parentSubject?.id) {
        data = await courseService.getAttachmentsBySubject(parentSubject.id);
      }
      if (!data || data.length === 0) {
        data = await courseService.getAttachmentsBySubject(subjectItem.id);
      }
      setLoadedAttachments(data || []);
    } catch (err) {
      console.error("Erreur chargement pièces jointes:", err);
      setLoadedAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleViewResources = async (subjectItem, parentSubject) => {
    setSelectedSubject(subjectItem);
    setViewMode("resources");
    setActiveTab("content");
    setResourcesLoading(true);
    try {
      let data = [];
      if (parentSubject?.id) {
        data = await courseService.getResourcesBySubject(parentSubject.id);
      }
      if (!data || data.length === 0) {
        data = await courseService.getResourcesBySubject(subjectItem.id);
      }
      setLoadedResources(data || []);
    } catch (err) {
      console.error("Erreur chargement ressources:", err);
      setLoadedResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const fetchQuizResults = async () => {
    try {
      // ✅ FIX : getQuizResults n'existe pas dans le service, skip silencieusement
      if (typeof courseService.getQuizResults !== 'function') {
        console.log("ℹ️ getQuizResults non disponible, skip");
        return;
      }
      const results = await courseService.getQuizResults(courseId);
      const mapped = {};
      results.forEach(r => { mapped[r.subjectId] = r.passed; });
      setQuizResults(mapped);
    } catch (err) { console.error("Erreur quiz results:", err); }
  };

  const groupByTitle = (groups) => {
    const map = new Map();
    groups.forEach((group) => {
      const key = group.title.trim();
      if (!map.has(key)) {
        map.set(key, { title: key, elementType: "MIXED", elementPosition: group.elementPosition, subjects: [], trainings: [], quizzes: [] });
      }
      const merged = map.get(key);
      (group.subjects || []).forEach((subject) => {
        const existing = merged.subjects.find(s => s.title === subject.title);
        if (existing) {
          if (!existing.children) existing.children = [];
          existing.children.push({ ...subject, title: subject.subTitle || subject.title });
        } else {
          merged.subjects.push({ ...subject, children: [] });
        }
      });
      merged.trainings.push(...(group.trainings || []));
      merged.quizzes.push(...(group.quizzes || []));
    });
    return Array.from(map.values()).sort((a, b) => a.elementPosition - b.elementPosition);
  };

  const reloadCourseStructure = async () => {
    try {
      setLoading(true);
      const data = await courseService.getCourseStructureLight(courseId);
      const mergedGroups = groupByTitle(data.groups || []);
      setCourseStructure(mergedGroups);
      setFlatList(flattenSubjects(data.groups || []));
      setViewMode("content");
      setActiveTab("content");

      if (data.groups?.length > 0) {
        const firstSubject = data.groups[0].subjects[0];
        let initialSubject;
        if (firstSubject?.children?.length > 0) {
          initialSubject = firstSubject.children[0];
          setSelectedSubject(initialSubject);
          setReadItems([initialSubject.title]);
          setOpenedCollapseKey([firstSubject.title]);
        } else {
          initialSubject = firstSubject;
          setSelectedSubject(initialSubject);
          setReadItems([initialSubject.title]);
          setOpenedCollapseKey([firstSubject.title]);
        }

        if (initialSubject?.id) {
          courseService.getSubjectContent(initialSubject.id).then(content => {
            setSelectedSubject(prev => prev?.id === initialSubject.id ? { ...prev, content } : prev);
            setFlatList(prev => prev.map(s => s.id === initialSubject.id ? { ...s, content } : s));
          }).catch(err => console.error("Erreur contenu reload:", err));
        }
      }

      await fetchQuizResults();
    } catch (err) { console.error("Erreur reload:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const loadCourseData = async () => {
      if (!courseId) return;
      try {
        setLoading(true);
        await courseService.getCourseBasic(courseId);
        const data = await courseService.getCourseStructureLight(courseId);
        const groups = data.groups || [];
        const mergedGroups = groupByTitle(groups);
        setCourseStructure(mergedGroups);
        setFlatList(flattenSubjects(groups));

        if (groups.length > 0) {
          const firstSubject = groups[0].subjects[0];
          let initialSubject;
          if (firstSubject?.children?.length > 0) {
            initialSubject = firstSubject.children[0];
            setSelectedSubject(initialSubject);
            setReadItems([initialSubject.title]);
          } else {
            initialSubject = firstSubject;
            setSelectedSubject(initialSubject);
            setReadItems([initialSubject.title]);
          }
          setOpenedCollapseKey([firstSubject.title]);
          setViewMode("content");
          setCurrentStep(0);
          setActiveTab("content");

          if (initialSubject?.id) {
            courseService.getSubjectContent(initialSubject.id).then(content => {
              setSelectedSubject(prev => prev?.id === initialSubject.id ? { ...prev, content } : prev);
              setFlatList(prev => prev.map(s => s.id === initialSubject.id ? { ...s, content } : s));
            }).catch(err => console.error("Erreur contenu initial:", err));
          }
        }

        Promise.all([
          courseService.getquizzes_of_cours(courseId).then(setCourseQuizzes),
          fetchQuizResults()
        ]).catch(err => console.error("Erreur quizzes:", err));
      } catch (error) { console.error("Erreur load:", error); }
      finally { setLoading(false); }
    };
    loadCourseData();
  }, [courseId]);

  const handleMarkAsRead = async (arg) => {
    let subject, index;
    if (typeof arg === 'number') { index = arg; subject = flatList[index]; }
    else { subject = arg; index = flatList.findIndex(s => s.id === subject.id); }
    if (!subject) return;

    let cleanSubject = { ...subject };
    if (subject.children?.length === 1 && subject.children[0].id === subject.id) {
      cleanSubject = { ...subject.children[0], title: subject.title, content: subject.content, comments: subject.comments };
    }

    const parentTitle = courseStructure.flatMap(chap => chap.subjects)
      .find(sub => sub.children?.some(child => child.id === cleanSubject.id))?.title;
    setOpenedCollapseKey(parentTitle ? [parentTitle] : [cleanSubject.title]);

    setSelectedSubject({
      ...cleanSubject,
      attachments: cleanSubject.attachments?.length ? cleanSubject.attachments : [],
      resources: cleanSubject.resources?.length ? cleanSubject.resources : [],
      comments: cleanSubject.comments || [],
    });

    const stepIndex = courseStructure.findIndex(chapter =>
      chapter.subjects.some(sub => sub.id === cleanSubject.id || (sub.children || []).some(child => child.id === cleanSubject.id))
    );
    setCurrentStep(stepIndex);
    setSelectedIndex(index);
    setFooterView(null);
    if (!readItems.includes(cleanSubject.title)) setReadItems((prev) => [...prev, cleanSubject.title]);
    setViewMode("content");
    if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    if (!cleanSubject.content && cleanSubject.id) {
      try {
        setContentLoading(true);
        const content = await courseService.getSubjectContent(cleanSubject.id);
        setSelectedSubject(prev => prev?.id === cleanSubject.id ? { ...prev, content } : prev);
        setFlatList(prev => prev.map(s => s.id === cleanSubject.id ? { ...s, content } : s));
      } catch (err) {
        console.error("Erreur chargement contenu:", err);
      } finally {
        setContentLoading(false);
      }
    }
  };

  const getQuizResultIcon = (subject) => {
    const result = quizResults[subject.id];
    if (result === true) return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} />;
    if (result === false) return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: '12px' }} />;
    return null;
  };

  const handleQuizSubmit = (subjectId, isCorrect) => {
    setQuizResults(prev => ({ ...prev, [subjectId]: isCorrect }));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <Spin size="large" tip="Chargement..." />
      </div>
    );
  }

  return (
    <div className="cv-outer-wrapper">
      {/* ✅ Bandeau Note Finale en haut */}
      {myNote > 0 && (
        <div className="cv-note-banner">
          <div className="cv-note-banner-content">
            <div className="cv-note-banner-left">
              <span className="cv-note-icon">📊</span>
              <span className="cv-note-label">Note Finale</span>
            </div>
            <div className={`cv-note-value ${myNote >= (threshold || 50) ? 'cv-note-pass' : 'cv-note-fail'}`}>
              {myNote.toFixed(2)} / 100
            </div>
          </div>
        </div>
      )}

    <div className="cv-wrapper">
     <style>{`
        .cv-outer-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 60vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ===== BANDEAU NOTE FINALE ===== */
        .cv-note-banner {
          background: white;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          padding: 10px 16px;
          margin-bottom: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }
        .cv-note-banner-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cv-note-banner-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cv-note-icon {
          font-size: 20px;
        }
        .cv-note-label {
          font-size: 14px;
          font-weight: 600;
          color: #595959;
        }
        .cv-note-value {
          font-size: 22px;
          font-weight: 700;
          padding: 6px 16px;
          border-radius: 8px;
        }
        .cv-note-pass {
          color: #52c41a;
          background: #f6ffed;
          border: 1px solid #b7eb8f;
        }
        .cv-note-fail {
          color: #ff4d4f;
          background: #fff2f0;
          border: 1px solid #ffccc7;
        }

        .cv-wrapper {
          display: flex;
          gap: 12px;
          min-height: 60vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .cv-sidebar {
          width: 320px;
          min-width: 320px;
          max-width: 320px;
          position: sticky;
          top: 10px;
          height: fit-content;
          max-height: 80vh;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        }

        .cv-sidebar-header {
          padding: 10px 12px;
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .cv-sidebar-header h4 {
          margin: 0;
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .cv-sidebar-content {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
          max-height: calc(80vh - 130px);
        }

        .cv-sidebar-content::-webkit-scrollbar { width: 4px; }
        .cv-sidebar-content::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 2px; }

        .cv-sidebar-footer {
          padding: 8px;
          border-top: 1px solid #f0f0f0;
          background: #fafafa;
          flex-shrink: 0;
        }

        .cv-chapter { margin-bottom: 6px; }

        .cv-chapter-title {
          font-size: 12px;
          font-weight: 600;
          color: #262626;
          padding: 6px 8px;
          background: #f5f5f5;
          border-radius: 4px;
          margin-bottom: 4px;
        }

        .cv-item {
          padding: 6px 8px;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #444;
          margin-bottom: 3px;
        }

        .cv-item:hover { background: #e6f7ff; }
        .cv-item.active { background: #e6f7ff; color: #1890ff; font-weight: 600; }
        .cv-item-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        .cv-child {
          padding: 6px 8px 6px 26px;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #444;
          margin-bottom: 3px;
        }

        .cv-child:hover { background: #e6f7ff; }
        .cv-child.active { background: #e6f7ff; color: #1890ff; font-weight: 600; }

        .cv-badges { display: flex; gap: 5px; margin-left: auto; align-items: center; }

        .cv-badge {
          font-size: 10px;
          padding: 2px 5px;
          border-radius: 3px;
          font-weight: 600;
        }
        .cv-badge.pj { background: #e6f7ff; color: #1890ff; }

        .cv-child-actions, .cv-item-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 4px 8px 8px 28px;
        }

        .cv-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 500;
          border: 1px solid;
          transition: all 0.2s;
        }

        .cv-action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .cv-action-btn.res {
          background: #f6ffed;
          border-color: #b7eb8f;
          color: #52c41a;
        }

        .cv-action-btn.pj {
          background: #e6f7ff;
          border-color: #91d5ff;
          color: #1890ff;
        }

        .cv-action-btn.quiz {
          background: #fffbe6;
          border-color: #ffe58f;
          color: #faad14;
        }

        .cv-footer-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 11px;
          font-weight: 500;
          border: 1px solid;
          margin-bottom: 4px;
        }
        .cv-footer-btn:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.12); }

        .cv-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .cv-content-card {
          background: white;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }

        .cv-content-header {
          padding: 10px 14px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
        }

        .cv-content-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #262626;
        }

        .cv-content-body {
          padding: 14px;
          font-size: 13px;
          line-height: 1.7;
          color: #434343;
        }

        .cv-content-body::-webkit-scrollbar { width: 5px; }
        .cv-content-body::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 3px; }

        .cv-content-nav {
          padding: 10px 14px;
          background: #fafafa;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .cv-content-card,
        .cv-content-body,
        .cv-main,
        .cv-wrapper {
          contain: none !important;
          overflow: visible !important;
        }

        .cv-resource-item {
          margin-bottom: 10px;
          padding: 14px 16px;
          background: #f6ffed;
          border-radius: 8px;
          border: 1px solid #b7eb8f;
          transition: all 0.2s;
        }
        .cv-resource-item:hover {
          box-shadow: 0 2px 8px rgba(82, 196, 26, 0.15);
        }
        .cv-resource-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cv-resource-link {
          font-size: 14px;
          font-weight: 600;
          color: #722ed1;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        .cv-resource-link:hover {
          color: #531dab;
          text-decoration: underline;
        }
        .cv-resource-title-text {
          font-size: 14px;
          font-weight: 600;
          color: #262626;
        }
        .cv-resource-extra {
          margin-top: 8px;
          padding-left: 28px;
          color: #595959;
          font-size: 13px;
        }

        .cv-sidebar .ant-collapse { background: transparent; border: none; }
        .cv-sidebar .ant-collapse-item { border: none !important; }
        .cv-sidebar .ant-collapse-header { padding: 6px 8px !important; font-size: 12px !important; min-height: auto !important; }
        .cv-sidebar .ant-collapse-content-box { padding: 0 !important; }
        .cv-sidebar .ant-collapse-expand-icon { font-size: 12px !important; }

        .cv-main .ant-tabs-tab { padding: 8px 14px !important; font-size: 12px !important; }
        .cv-main .ant-tabs-nav { margin-bottom: 10px !important; }

        @media (max-width: 768px) {
          .cv-wrapper { flex-direction: column; }
          .cv-sidebar { width: 100%; max-width: 100%; position: relative; max-height: 35vh; }
        }
      `}</style>

      {/* ===== SIDEBAR ===== */}
      <aside className="cv-sidebar">
        <div className="cv-sidebar-header">
          <h4><BookOutlined /> Sommaire</h4>
          <Button size="small" type="text" onClick={reloadCourseStructure} loading={loading} icon={<ReloadOutlined style={{ fontSize: 11 }} />} style={{ color: 'white', height: 22, width: 22, padding: 0 }} />
        </div>

        <div className="cv-sidebar-content">
          {courseStructure.map((chapter, chapterIndex) => {
            const isTrainingGroup = chapter.elementType === "TRAINING" || chapter.elementType === "MIXED";
            const isQuizGroup = chapter.elementType === "QUIZ" || chapter.elementType === "MIXED";

            if (isTrainingGroup && chapter.trainings.length > 0) {
              return chapter.trainings.map((training, tIndex) => (
                <div key={`t-${chapterIndex}-${tIndex}`} className={`cv-item ${selectedSubject?.id === training.id ? 'active' : ''}`}
                  onClick={() => { setSelectedSubject({ ...training, courseId }); setReadItems(prev => [...prev, training.title]); setViewMode("training"); setFooterView(null); }}>
                  <PlayCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                  <span className="cv-item-text">{training.title}</span>
                  {readItems.includes(training.title) && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                </div>
              ));
            }

            if (isQuizGroup && chapter.quizzes?.length > 0) {
              return chapter.quizzes.map((quiz, qIndex) => (
                <div key={`q-${chapterIndex}-${qIndex}`} className={`cv-item ${selectedSubject?.id === quiz.id ? 'active' : ''}`}
                  onClick={() => { setSelectedSubject({ ...quiz }); setViewMode("quiz_course"); setFooterView(null); if (!readItems.includes(quiz.title)) setReadItems(prev => [...prev, quiz.title]); }}>
                  <ThunderboltOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
                  <span className="cv-item-text">{quiz.title}</span>
                  {readItems.includes(quiz.title) && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                </div>
              ));
            }

            return (
              <div key={chapterIndex} className="cv-chapter">
                <div className="cv-chapter-title">{chapter.title}</div>
                {chapter.subjects.map((subject, subjectIndex) => {
                  const children = subject.children || [];

                  if (children.length > 0) {
                    return (
                      <Collapse key={subjectIndex} bordered={false} size="small" activeKey={openedCollapseKey} onChange={setOpenedCollapseKey} style={{ background: 'transparent' }}>
                        <Panel header={<span style={{ fontSize: 12, fontWeight: 500 }}>{subject.title}</span>} key={subject.title} style={{ border: 'none' }}>
                          {(subject.resources?.length > 0 || subject.attachments?.length > 0) && (
                            <div className="cv-child-actions">
                              {subject.resources?.length > 0 && (
                                <div className="cv-action-btn res" onClick={() => handleViewResources(subject)}>
                                  <ReadOutlined /> Ressources
                                </div>
                              )}
                              {subject.attachments?.length > 0 && (
                                <div className="cv-action-btn pj" onClick={() => handleViewAttachments(subject)}>
                                  <PaperClipOutlined /> Pièces jointes
                                </div>
                              )}
                            </div>
                          )}

                          {children.map((child, childIndex) => (
                            <div key={childIndex}>
                              <div className={`cv-child ${selectedSubject?.id === child.id ? 'active' : ''}`} onClick={() => handleMarkAsRead(child)}>
                                <span className="cv-item-text">{child.title}</span>
                                <div className="cv-badges">
                                  {getQuizResultIcon(child)}
                                  {readItems.includes(child.title) && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                                </div>
                              </div>
                              <div className="cv-child-actions">
                                {child.resources?.length > 0 && !subject.resources?.length && (
                                  <div className="cv-action-btn res" onClick={() => handleViewResources(child, subject)}>
                                    <ReadOutlined /> Ressources
                                  </div>
                                )}
                                {child.attachments?.length > 0 && !subject.attachments?.length && (
                                  <div className="cv-action-btn pj" onClick={() => handleViewAttachments(child, subject)}>
                                    <PaperClipOutlined /> Pièces jointes
                                  </div>
                                )}
                                {child.quizzes?.length > 0 && (
                                  <div className="cv-action-btn quiz" onClick={() => { setSelectedSubject(child); setViewMode("quiz"); }}>
                                    <QuestionCircleOutlined /> Quiz
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </Panel>
                      </Collapse>
                    );
                  }

                  return (
                    <div key={subjectIndex}>
                      <div className={`cv-item ${selectedSubject?.id === subject.id ? 'active' : ''}`} onClick={() => handleMarkAsRead(subject)}>
                        <span className="cv-item-text">{subject.title}</span>
                        <div className="cv-badges">
                          {getQuizResultIcon(subject)}
                          {readItems.includes(subject.title) && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                        </div>
                      </div>
                      <div className="cv-item-actions">
                        {subject.resources?.length > 0 && (
                          <div className="cv-action-btn res" onClick={() => handleViewResources(subject)}>
                            <ReadOutlined /> Ressources
                          </div>
                        )}
                        {subject.attachments?.length > 0 && (
                          <div className="cv-action-btn pj" onClick={() => handleViewAttachments(subject)}>
                            <PaperClipOutlined /> Pièces jointes
                          </div>
                        )}
                        {subject.quizzes?.length > 0 && (
                          <div className="cv-action-btn quiz" onClick={() => { setSelectedSubject(subject); setViewMode("quiz"); }}>
                            <QuestionCircleOutlined /> Quiz
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="cv-sidebar-footer">
          {footerElements.map((el, idx) => (
            <div key={idx} className="cv-footer-btn" style={{ background: el.bgColor, borderColor: el.borderColor, color: el.color }}
              onClick={() => { setFooterView(el.key); setActiveTab("content"); setSelectedSubject(null); }}>
              {el.icon} {el.label}
            </div>
          ))}
        </div>
      </aside>

      {/* ===== CONTENU ===== */}
      <main className="cv-main" ref={contentRef}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small"
          items={[
            { key: 'content', label: <span><ReadOutlined /> Contenu</span> },
            { key: 'comments', label: <span><MessageOutlined /> Commentaires</span> }
          ]} />

        {viewMode === "content" && activeTab === "content" && selectedSubject && selectedSubject.elementType !== "TRAINING" && (
          <div className="cv-content-card">
            <div className="cv-content-header"><h3>{selectedSubject.title}</h3></div>
            <div className="cv-content-body">
              {contentLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Spin tip="Chargement du contenu..." />
                </div>
              ) : (
                <>
                  {selectedSubject.content && <SecureContent html={selectedSubject.content} />}

                  {selectedSubject.attachments?.length > 0 && (
                    <div style={{ marginTop: 12, padding: 10, background: '#f6f8fa', borderRadius: 6 }}>
                      <Text strong style={{ fontSize: 12 }}><PaperClipOutlined /> Pièces jointes</Text>
                      <div style={{ marginTop: 6 }}>
                        {selectedSubject.attachments.map((file, i) => (
                          <a key={i} href={file.fileUrl} target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: 12, color: '#1890ff', marginBottom: 3 }}>{file.fileName}</a>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSubject.resources?.length > 0 && (
                    <div style={{ marginTop: 12, padding: 10, background: '#f6ffed', borderRadius: 6 }}>
                      <Text strong style={{ fontSize: 12 }}><ReadOutlined /> Ressources</Text>
                      <div style={{ marginTop: 6 }}>
                        {selectedSubject.resources.map((res, i) => {
                          const { link, cleanContent } = extractLinkFromContent(res.content);
                          return (
                            <div key={i} style={{ marginBottom: 6 }}>
                              {link ? (
                                <a href={link} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#722ed1', textDecoration: 'none' }}>
                                  <BookOutlined style={{ marginRight: 6 }} />{res.title} <LinkOutlined style={{ fontSize: 11, opacity: 0.6 }} />
                                </a>
                              ) : (
                                <Text strong style={{ fontSize: 12, color: '#389e0d' }}><BookOutlined /> {res.title}</Text>
                              )}
                              {cleanContent && <div style={{ paddingLeft: 20, marginTop: 4 }}><SecureContent html={cleanContent} /></div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedSubject.quizzes?.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <Button size="small" type="primary" icon={<QuestionCircleOutlined />} onClick={() => setViewMode("quiz")} style={{ background: '#faad14', borderColor: '#faad14', fontSize: 12 }}>Passer le Quiz</Button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="cv-content-nav">
              <Button size="small" disabled={selectedIndex === 0} onClick={() => selectedIndex > 0 && handleMarkAsRead(selectedIndex - 1)} icon={<LeftOutlined />} style={{ fontSize: 11 }}>Préc.</Button>
              <Text type="secondary" style={{ fontSize: 11 }}>{selectedIndex + 1} / {flatList.length}</Text>
              <Button size="small" type="primary" disabled={selectedIndex >= flatList.length - 1} onClick={() => selectedIndex < flatList.length - 1 && handleMarkAsRead(selectedIndex + 1)} style={{ fontSize: 11 }}>Suiv. <RightOutlined /></Button>
            </div>
          </div>
        )}

        {activeTab === "content" && viewMode === "training" && selectedSubject && (
          <div className="cv-content-card"><TrainingViewer courseId={selectedSubject.courseId} trainingId={selectedSubject.id} /></div>
        )}

        {viewMode === "quiz" && selectedSubject && (
          <div className="cv-content-card"><QuizzesSubject subject={selectedSubject} courseId={courseId} onSubmitResult={handleQuizSubmit} /></div>
        )}

        {viewMode === "quiz_course" && selectedSubject?.id && (
          <div className="cv-content-card"><TestDeConnaissance quizId={selectedSubject.id} courseId={courseId} attemptOnce={selectedSubject.attemptOnce} onSubmitResult={handleQuizSubmit} /></div>
        )}

        {viewMode === "attachments" && (
          <div className="cv-content-card">
            <div className="cv-content-header">
              <h3><PaperClipOutlined /> Pièces jointes - {selectedSubject?.title}</h3>
            </div>
            <div className="cv-content-body">
              {attachmentsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Spin tip="Chargement des pièces jointes..." />
                </div>
              ) : loadedAttachments.length > 0 ? (
                loadedAttachments.map((file, i) => (
                  <a key={file.id || i} href={file.fileUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#1890ff', marginBottom: 8, padding: '10px 14px', background: '#f0f5ff', borderRadius: 6, border: '1px solid #adc6ff', textDecoration: 'none' }}>
                    <PaperClipOutlined style={{ fontSize: 16 }} />
                    <span style={{ flex: 1 }}>{file.fileName}</span>
                    <span style={{ fontSize: 11, color: '#999' }}>Ouvrir</span>
                  </a>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                  Aucune pièce jointe disponible
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === "resources" && (
          <div className="cv-content-card">
            <div className="cv-content-header"><h3><ReadOutlined /> Ressources - {selectedSubject?.title}</h3></div>
            <div className="cv-content-body">
              {resourcesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Spin tip="Chargement des ressources..." />
                </div>
              ) : loadedResources.length > 0 ? (
                loadedResources.map((res, i) => {
                  const { link, cleanContent } = extractLinkFromContent(res.content);
                  return (
                    <div key={res.id || i} className="cv-resource-item">
                      <div className="cv-resource-title-row">
                        <BookOutlined style={{ fontSize: 16, color: '#722ed1' }} />
                        {link ? (
                          <a href={link} target="_blank" rel="noopener noreferrer" className="cv-resource-link">
                            {res.title}
                            <LinkOutlined style={{ fontSize: 12, opacity: 0.5 }} />
                          </a>
                        ) : (
                          <span className="cv-resource-title-text">{res.title}</span>
                        )}
                      </div>
                      {cleanContent && cleanContent.trim() && (
                        <div className="cv-resource-extra">
                          <SecureContent html={cleanContent} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                  Aucune ressource disponible
                </div>
              )}
            </div>
          </div>
        )}

        {footerView === "eval" && (
          <div className="cv-content-card"><div style={{ padding: 14 }}><EvaluationPreviewComponent quizId={0} courseId={courseId} attemptOnce={false} /></div></div>
        )}

        {footerView === "certif" && (
          <div className="cv-content-card">
            <div style={{ padding: 14 }}>
              {myNote > threshold ? (
                <Alert type="success" message="Félicitations !" description={
                  <div>
                    <p style={{ fontSize: 13 }}>Vous êtes éligible pour votre certificat.</p>
                    <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={() => setIsModalVisible(true)} style={{ marginTop: 6, fontSize: 12 }}>Télécharger</Button>
                    <Modal title="🎓 Certificat" open={isModalVisible} onCancel={() => setIsModalVisible(false)} footer={null} width={800} centered>
                      <GeneratedCertificate courseId={courseId} userId={userId} />
                    </Modal>
                  </div>
                } />
              ) : (
                <Alert type="warning" message="Certificat non disponible" description={<span style={{ fontSize: 12 }}>Note: {myNote} | Requise: {threshold}</span>} />
              )}
            </div>
          </div>
        )}

        {footerView === "badge" && (
          <div className="cv-content-card"><BadgeEligibility courseId={courseId} myNote={myNote} courseTitle={courseTitle} userId={userId} /></div>
        )}

        {activeTab === "comments" && selectedSubject && (
          <div className="cv-content-card">
            <div className="cv-content-header"><h3>Commentaires - {selectedSubject.title}</h3></div>
            <div className="cv-content-body">
              {(selectedSubject.comments || []).map((c, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 10, padding: 8, background: '#f9f9f9', borderRadius: 4 }}>
                  <Avatar src={c.avatar} size={28} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>{c.author}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{c.content}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{c.date}</div>
                  </div>
                </div>
              ))}
              <Input.TextArea rows={2} value={commentInput} onChange={(e) => setCommentInput(e.target.value)} placeholder="Ajouter un commentaire..." style={{ fontSize: 12 }} />
              <Button size="small" type="primary" icon={<SendOutlined />} style={{ marginTop: 6, fontSize: 11 }}
                onClick={() => {
                  if (!commentInput.trim()) return;
                  setSelectedSubject({ ...selectedSubject, comments: [...(selectedSubject.comments || []), { author: "Vous", avatar: "https://i.pravatar.cc/150?u=you", content: commentInput.trim(), date: "À l'instant" }] });
                  setCommentInput("");
                }}>Envoyer</Button>
            </div>
          </div>
        )}
      </main>
    </div>
    </div>
  );
};

export default CourseFullViewer;